/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo } from 'react';
import { FC, ReactNode } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Hidden,
  List,
  ListSubheader,
  Typography,
  makeStyles
} from '@material-ui/core';
import {
  UserCheck as UserCheckIcon,
  PieChart as PieChartIcon,
  User as UserIcon,
  Smartphone as CaptivePortalIcon,
  Video as VideoIcon,
  Tool as ToolIcon,
  Users as UsersIcon,
  Calendar as CalendarIcon,
  Radio as RadioIcon,
  Wifi as WifiIcon,
  Home as HomeIcon,
  MapPin as LocationIcon,
  Activity as ActivityIcon,
  Image as ImageIcon,
  Map as MapIcon,
  Thermometer as ThermometerIcon,
  Globe as GlobeIcon,
  Target as TargetIcon
} from 'react-feather';
import Logo from 'src/components/Logo';
import useAuth from 'src/hooks/useAuth';
import NavItem from './NavItem';
import { getRoles } from 'src/contexts/Auth0Context';
import { IRole } from 'src/types/global';
import { gql, useQuery } from '@apollo/client';
import { ShowHideOverviewQuery as Q } from 'src/types/graphql';
import LoadingScreen from 'src/components/LoadingScreen';
import { applyMenuFilters, normalizeRole } from './helpers';
import { useBrand } from 'src/contexts/BrandContext';

interface NavBarProps {
  onMobileClose: () => void;
  openMobile: boolean;
}

interface Item {
  href?: string;
  icon?: ReactNode;
  info?: ReactNode;
  items?: Item[];
  title: string;
  adminOnly: boolean;
  managerOnly: boolean;
  managerOnlyStrict?: boolean;
  disabled?: boolean;
  featureKey?: string;
}

interface Section {
  items: Item[];
  subheader: string;
}

const sections: () => Section[] = () => {
  return [
    {
      subheader: 'Reports',
      items: [
        {
          title: 'Dashboard',
          icon: PieChartIcon,
          href: '/app/dashboard/overview',
          adminOnly: false,
          managerOnly: false,
          featureKey: 'dashboard'
        },
        {
          title: 'Overview',
          icon: GlobeIcon,
          href: '/app/brand/overview',
          adminOnly: false,
          managerOnly: false,
          featureKey: 'overview'
        },
        {
          title: 'Zoning',
          icon: MapIcon,
          href: '/app/zoning/overview',
          adminOnly: false,
          managerOnly: false,
          disabled: false,
          featureKey: 'zoning'
        },
        {
          title: 'Checkins',
          icon: UserCheckIcon,
          href: '/app/checkins',
          adminOnly: false,
          managerOnly: false,
          disabled: false,
          featureKey: 'checkins',
          items: [
            {
              title: 'Profiles',
              icon: UserIcon,
              href: '/app/checkins/profiles',
              adminOnly: false,
              managerOnly: false,
              featureKey: 'checkins'
            },
            {
              title: 'Captive Portal',
              icon: CaptivePortalIcon,
              href: '/app/checkins/captive-portal',
              adminOnly: false,
              managerOnly: false,
              featureKey: 'checkins'
            }
          ]
        },
        {
          title: 'Real-Time Occupancy',
          icon: VideoIcon,
          href: '/app/realtime/overview',
          adminOnly: false,
          managerOnly: false,
          disabled: false,
          featureKey: 'real_time_occupancy'
        },
        {
          title: 'Point of Interest',
          icon: LocationIcon,
          href: '/app/poi/overview',
          adminOnly: false,
          managerOnly: false,
          disabled: false,
          featureKey: 'poi'
        },
        {
          title: 'Queue Management',
          icon: UsersIcon,
          href: '/app/queue/overview',
          adminOnly: false,
          managerOnly: true,
          disabled: false,
          featureKey: 'queue_management'
        },
        {
          title: 'Heatmap',
          icon: ThermometerIcon,
          href: '/app/heatmap/overview',
          adminOnly: false,
          managerOnly: false,
          disabled: false,
          featureKey: 'heatmap'
        },
        {
          title: 'Sensors',
          icon: ActivityIcon,
          href: '/app/sensors/overview',
          adminOnly: true,
          managerOnly: true,
          disabled: true,
          featureKey: 'sensors'
        },
        {
          title: 'Snapshots',
          icon: ImageIcon,
          href: '/app/snapshots/overview',
          adminOnly: true,
          managerOnly: true,
          disabled: true,
          featureKey: 'snapshots'
        },
        {
          title: 'Manage',
          icon: ToolIcon,
          href: '/app/manage',
          adminOnly: false,
          managerOnly: true,
          featureKey: 'manage',
          items: [
            {
              title: 'Users',
              icon: UsersIcon,
              href: '/app/manage/users',
              adminOnly: false,
              managerOnly: true,
              featureKey: 'users_mgmt'
            },
            {
              title: 'Brands',
              icon: HomeIcon,
              href: '/app/manage/brands',
              adminOnly: true,
              managerOnly: true,
              featureKey: 'brands'
            },
            {
              title: 'Locations',
              icon: LocationIcon,
              href: '/app/manage/locations',
              adminOnly: true,
              managerOnly: true,
              featureKey: 'locations'
            },
            {
              title: 'Zones Configuration',
              icon: RadioIcon,
              href: '/app/manage/zones',
              adminOnly: true,
              managerOnly: true,
              featureKey: 'zones_config'
            },
            {
              title: 'Business Hours',
              icon: CalendarIcon,
              href: '/app/manage/business-hours',
              adminOnly: false,
              managerOnly: true,
              featureKey: 'business_hours'
            },
            {
              title: 'Access Points',
              icon: WifiIcon,
              href: '/app/manage/access-points',
              adminOnly: true,
              managerOnly: true,
              featureKey: 'access_points'
            },
            {
              title: 'Cameras',
              icon: VideoIcon,
              href: '/app/manage/cameras',
              adminOnly: true,
              managerOnly: true,
              featureKey: 'cameras'
            },
            {
              title: 'Camera Settings',
              icon: VideoIcon,
              href: '/app/cameras/overview',
              adminOnly: false,
              managerOnly: false,
              managerOnlyStrict: true,
              featureKey: 'camera_setting'
            },
            {
              title: 'Camera Monitoring',
              icon: VideoIcon,
              href: '/app/manage/cameras/monitoring',
              adminOnly: false,
              managerOnly: true,
              featureKey: 'camera_monitoring'
            },
            {
              title: 'Point of Interest',
              icon: TargetIcon,
              href: '/app/manage/poi',
              adminOnly: false,
              managerOnly: true,
              featureKey: 'poi' // same feature as top-level POI
            }
          ]
        }
      ]
    }
  ];
};

function renderNavItems({
  items,
  pathname,
  depth = 0,
  t,
  role
}: {
  role: string;
  items: Item[];
  pathname: string;
  depth?: number;
  t: TFunction;
}) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) =>
          reduceChildRoutes({
            acc,
            item,
            pathname,
            depth,
            t,
            role
          }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  pathname,
  item,
  depth,
  t,
  role
}: {
  acc: any[];
  pathname: string;
  item: Item;
  depth: number;
  role: string;
  t: TFunction;
}) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={t(item.title)}
        disabled={item.disabled}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
          t,
          role
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={t(item.title)}
        disabled={item.disabled}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  }
}));

export const SHOW_HIDE_PREQUERY = gql`
  query ShowHideOverview($brandId: Int!) {
    brand(id: $brandId) {
      id
      name
      hasZoning
      hasHeatmap
      hasSensors
      hasCheckins
      hasSnapshots
      hasCameraSetting
      hasPointOfInterest
      hasQueueManagement
      hasRealTimeOccupancy
    }
  }
`;

const NavBar: FC<NavBarProps> = ({ onMobileClose, openMobile }) => {
  const classes = useStyles();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { brandId } = useBrand();
  const [counter, setCounter] = useState(1);
  const { isDemo } = useAuth();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  let role = 'cms_user';
  role = normalizeRole(getRoles());

  let _sections: any = [...sections()];

  // Use Apollo Client's useQuery hook to fetch data based on brandId
  const { data } = useQuery(SHOW_HIDE_PREQUERY, {
    variables: { brandId },
    skip: !brandId,
    fetchPolicy: 'network-only'
  });

  if (data?.brand) {
    const showHide = {
      zoning: data?.brand?.hasZoning,
      heatmap: data?.brand?.hasHeatmap,
      sensors: data?.brand?.hasSensors,
      checkins: data?.brand?.hasCheckins,
      snapshots: data?.brand?.hasSnapshots,
      camera_setting: data?.brand?.hasCameraSetting,
      poi: data?.brand?.hasPointOfInterest,
      queue_management: data?.brand?.hasQueueManagement,
      real_time_occupancy: data?.brand?.hasRealTimeOccupancy,
      manage: true,
      overview: !isDemo
    };

    _sections = applyMenuFilters(_sections, showHide, role);
  } else {
    _sections = applyMenuFilters(
      _sections,
      {
        zoning: false,
        heatmap: false,
        sensors: false,
        checkins: false,
        snapshots: false,
        camera_setting: false,
        poi: false,
        queue_management: false,
        real_time_occupancy: false,
        manage: false,
        overview: false
      },
      role
    );
  }

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box p={2} display="flex" justifyContent="center">
            <RouterLink to="/">
              <Logo />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2}>
          <Box display="flex" justifyContent="center">
            <Avatar alt="User" className={classes.avatar} src={user.avatar} />
          </Box>
          <Box mt={2} textAlign="center">
            <Typography variant="h5" color="textPrimary">
              {user.name}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box p={2}>
          {_sections.map(section => (
            <List
              key={section.subheader}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {t(section.subheader)}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname,
                t,
                role
              })}
            </List>
          ))}
        </Box>
        <Divider />
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
      <div id={brandId + '-set'} />
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
